/* oxlint-disable react/no-danger */

import { clsx } from 'clsx';
import { type FC } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { Ticker } from 'shared/components/Ticker';

import { type TGetMainpageBrandCompleteContextResult } from '../../utils/getMainpageBrandCompleteContext';

import styles from './MainpageBrandDesktop.css';

export type TMainpageBrandDesktopProps = {
  completeContext: TGetMainpageBrandCompleteContextResult;
};

/**
 * Десктопный баннер главной (`mainpageBrand`).
 *
 * Перенесён из frontend-mainpage (`MediaBanner`). В отличие от оригинала, где фон,
 * пометка «Реклама» и бегущая строка рисовались отдельно (FiltersBackground / SearchBlock /
 * Marquee в SearchBlockContainer), здесь весь визуал собран в одном баннере:
 * фон, лейбл, оффер, бегущая строка (Ticker вместо Marquee) и ипотечная сноска.
 */
export const MainpageBrandDesktop: FC<TMainpageBrandDesktopProps> = ({
  completeContext: {
    url,
    bgImageUrl,
    logoImageUrl,
    substrateNotNeeded,
    buttonText,
    label,
    note,
    sanitizedName,
    sanitizedInfo,
    sanitizedActionText,
    sanitizedPhone,
    mortgageWarning,
    mortgageTermsWarning,
    sizes,
  },
}) => {
  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <div className={styles['container']} style={sizes} data-testid="mainpage-brand-desktop">
      <div className={styles['background']}>
        <img className={styles['background-image']} src={bgImageUrl} alt="" />
      </div>

      {!!label && (
        <div className={styles['label']}>
          <AdLabel label={label} />
        </div>
      )}

      <a className={styles['promo']} href={url} target="_blank" rel="noopener noreferrer" data-testid="promo">
        <div className={styles['promo-body']}>
          {!!sanitizedActionText && (
            <span className={styles['ribbon']} dangerouslySetInnerHTML={{ __html: sanitizedActionText }} />
          )}

          <div className={styles['offer']}>
            <span className={clsx(styles['logo'], !substrateNotNeeded && styles['logo--substrate'])}>
              <img src={logoImageUrl} alt="logo" />
            </span>

            <div className={styles['details']}>
              {!!sanitizedName && (
                <h3 className={styles['title']} dangerouslySetInnerHTML={{ __html: sanitizedName }} />
              )}
              {!!sanitizedInfo && (
                <p className={styles['subtitle']} dangerouslySetInnerHTML={{ __html: sanitizedInfo }} />
              )}

              <div className={styles['actions']}>
                <span className={styles['button']}>{buttonText}</span>
                {!!sanitizedPhone && (
                  <span className={styles['phone']} dangerouslySetInnerHTML={{ __html: sanitizedPhone }} />
                )}
              </div>
            </div>
          </div>

          {!!note && (
            <div className={styles['note']}>
              <Ticker text={note} theme="light" withoutPadding pauseOnHover />
            </div>
          )}
        </div>

        {withMortgageAnnotations && (
          <div className={styles['mortgage-disclaimer']} data-testid="mortgage-disclaimer">
            <MortgageAnnotations
              height={sizes.height}
              mortgageWarning={mortgageWarning}
              mortgageTermsWarning={mortgageTermsWarning}
            />
          </div>
        )}
      </a>
    </div>
  );
};
